import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { eventSeriesAPI, slotsAPI } from '../../services/api';
import type { TrainingType, EventSeries, TrainingSlot, RecurrencePattern, CancellationInfo } from '../../types';
import BookingsModal from './BookingsModal';
import DefaultAssignmentsModal from './DefaultAssignmentsModal';
import EditChildModal from './EditChildModal';
import CancellationRecipientsModal from './CancellationRecipientsModal';
import { format } from 'date-fns';
import { useToast } from '../../context/ToastContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useTranslation } from 'react-i18next';

const PAGE_SIZE = 10;

const COLOR_PALETTE = [
  '#DBEAFE', '#D1FAE5', '#FFEDD5', '#EDE9FE', '#CCFBF1', '#FEE2E2',
  '#FCE7F3', '#FEF3C7', '#ECFCCB', '#CFFAFE', '#E0E7FF', '#F1F5F9',
];

const TYPE_DEFAULT_COLORS: Record<string, string> = {
  SWIMMING: '#DBEAFE',
  BIKING: '#D1FAE5',
  RUNNING: '#FFEDD5',
  GYM: '#EDE9FE',
  FREE: '#CCFBF1',
};

const EventsTab: React.FC<{ initialEditSeriesId?: string | null }> = ({ initialEditSeriesId }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingSeries, setEditingSeries] = useState<EventSeries | null>(null);
  const [expandedSeriesId, setExpandedSeriesId] = useState<string | null>(null);
  const [viewingBookings, setViewingBookings] = useState<{ id: string; title: string } | null>(null);
  const [viewingDefaultAssignments, setViewingDefaultAssignments] = useState<{ id: string; title: string; capacity: number } | null>(null);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [customColorStaging, setCustomColorStaging] = useState('#DBEAFE');
  const [recentColors, setRecentColors] = useState<string[]>([]);
  const [editingChild, setEditingChild] = useState<any | null>(null);
  const [cancellationInfo, setCancellationInfo] = useState<CancellationInfo | null>(null);
  const [openSeriesMenu, setOpenSeriesMenu] = useState<string | null>(null);
  const [seriesMenuPos, setSeriesMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [openChildMenu, setOpenChildMenu] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<{ created: number; failed: number; failures: any[] } | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [eventColorFilter, setEventColorFilter] = useState('');
  const [eventColorDropdownOpen, setEventColorDropdownOpen] = useState(false);
  const eventColorDropdownRef = useRef<HTMLDivElement>(null);
  const exportRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterType, setFilterType] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState<'date' | 'type' | 'title' | 'info' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
      if (eventColorDropdownRef.current && !eventColorDropdownRef.current.contains(e.target as Node)) {
        setEventColorDropdownOpen(false);
      }
      // Close kebab menus on click outside
      const target = e.target as HTMLElement;
      if (!target.closest('[data-kebab]')) {
        setOpenSeriesMenu(null);
        setOpenChildMenu(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const { data: seriesList = [] } = useQuery({
    queryKey: ['event-series'],
    queryFn: eventSeriesAPI.getAll,
  });

  // Open series for editing when navigated from another tab
  useEffect(() => {
    if (initialEditSeriesId && seriesList.length > 0) {
      const series = seriesList.find((s: EventSeries) => s.id === initialEditSeriesId);
      if (series) {
        setEditingSeries(series);
        setSelectedColor(series.color || null);
        setShowForm(true);
      }
    }
  }, [initialEditSeriesId, seriesList]);

  // Track recently used custom colors (max 3, deduplicated)
  const addRecentColor = (color: string) => {
    setRecentColors(prev => [color, ...prev.filter(c => c !== color)].slice(0, 3));
  };

  // Collect unique colors already used by existing series
  const existingEventColors = Array.from(
    new Set(seriesList.filter(s => s.color).map(s => s.color as string))
  );

  // Series with colors for the searchable "From events" dropdown
  const DAYS = [t('admin.sunday'), t('admin.monday'), t('admin.tuesday'), t('admin.wednesday'), t('admin.thursday'), t('admin.friday'), t('admin.saturday')];
  const seriesWithColors = useMemo(() => {
    return seriesList
      .filter(s => s.color)
      .map(s => {
        const day = DAYS[new Date(s.startDate).getDay()];
        const label = `${s.title} ${s.startTime}${s.endTime ? '-' + s.endTime : ''} ${day}`;
        return { ...s, day, label };
      });
  }, [seriesList]);

  const filteredSeriesColors = useMemo(() => {
    if (!eventColorFilter.trim()) return seriesWithColors;
    const q = eventColorFilter.toLowerCase();
    return seriesWithColors.filter(s => s.label.toLowerCase().includes(q) || s.type.toLowerCase().includes(q));
  }, [seriesWithColors, eventColorFilter]);

  // Fetch children when a series is expanded
  const { data: expandedChildren = [] } = useQuery({
    queryKey: ['series-children', expandedSeriesId],
    queryFn: () => eventSeriesAPI.getChildren(expandedSeriesId!),
    enabled: !!expandedSeriesId,
  });

  // Filter and sort series
  const filteredSeries = useMemo(() => {
    let result = seriesList;
    if (filterType !== 'ALL') {
      result = result.filter(s => s.type === filterType);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(s =>
        s.title.toLowerCase().includes(q) ||
        s.type.toLowerCase().includes(q) ||
        s.description?.toLowerCase().includes(q)
      );
    }
    if (sortColumn) {
      const dir = sortDirection === 'asc' ? 1 : -1;
      result = [...result].sort((a, b) => {
        switch (sortColumn) {
          case 'date': {
            // Sort by day of week (Monday=0 ... Sunday=6), then by time
            const dayA = (new Date(a.startDate).getDay() + 6) % 7;
            const dayB = (new Date(b.startDate).getDay() + 6) % 7;
            if (dayA !== dayB) return (dayA - dayB) * dir;
            return (a.startTime || '').localeCompare(b.startTime || '') * dir;
          }
          case 'type':
            return a.type.localeCompare(b.type) * dir;
          case 'title':
            return a.title.localeCompare(b.title) * dir;
          case 'info': {
            const ca = a.capacity ?? 0;
            const cb = b.capacity ?? 0;
            return (ca - cb) * dir;
          }
          default:
            return 0;
        }
      });
    }
    return result;
  }, [seriesList, filterType, searchQuery, sortColumn, sortDirection]);

  const handleSort = (column: 'date' | 'type' | 'title' | 'info') => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredSeries.length / PAGE_SIZE));
  const paginatedSeries = filteredSeries.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  // Reset to page 1 when filters or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, searchQuery, sortColumn, sortDirection]);

  useEffect(() => {
    if (showForm) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [showForm]);

  const createMutation = useMutation({
    mutationFn: eventSeriesAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-series'] });
      queryClient.invalidateQueries({ queryKey: ['slots'] });
      setShowForm(false);
      setEditingSeries(null);
      toast.success(t('admin.eventCreatedSuccess'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => eventSeriesAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-series'] });
      queryClient.invalidateQueries({ queryKey: ['series-children'] });
      queryClient.invalidateQueries({ queryKey: ['slots'] });
      setEditingSeries(null);
      setShowForm(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: eventSeriesAPI.delete,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event-series'] });
      queryClient.invalidateQueries({ queryKey: ['slots'] });
      toast.success(t('admin.eventSeriesDeleted'));
      if (data.cancellation) {
        setCancellationInfo(data.cancellation);
      }
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.error || t('admin.failedDeleteSeries');
      toast.error(msg);
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (slotId: string) => slotsAPI.updateSlot(slotId, { cancelled: false } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['series-children'] });
      queryClient.invalidateQueries({ queryKey: ['slots'] });
      toast.success(t('admin.eventRestored'));
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.error || t('admin.failedRestoreEvent');
      toast.error(msg);
    },
  });

  const bulkUploadMutation = useMutation({
    mutationFn: slotsAPI.bulkCreateSlots,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event-series'] });
      queryClient.invalidateQueries({ queryKey: ['slots'] });
      setUploadResult(data);
    },
    onError: (error: any) => {
      alert(t('admin.uploadFailed', { error: error?.response?.data?.error || error.message }));
    },
  });

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      bulkUploadMutation.mutate(file);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {
      type: formData.get('type') as TrainingType,
      title: formData.get('title') as string,
      description: formData.get('description') as string,
      date: formData.get('date') as string,
      startTime: formData.get('startTime') as string,
      endTime: (formData.get('endTime') as string) || undefined,
      capacity: parseInt(formData.get('capacity') as string),
      color: editingSeries ? (selectedColor || null) : (selectedColor || undefined),
      recurrencePattern: (formData.get('recurrencePattern') as RecurrencePattern) || 'NONE',
      recurrenceEndDate: formData.get('recurrenceEndDate') as string || undefined,
      recurrenceCount: formData.get('recurrenceCount')
        ? parseInt(formData.get('recurrenceCount') as string)
        : undefined,
    };

    if (editingSeries) {
      updateMutation.mutate({ id: editingSeries.id, data });
    } else {
      createMutation.mutate(data as any);
    }
  };

  const handleEdit = (series: EventSeries) => {
    setEditingSeries(series);
    setSelectedColor(series.color || null);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingSeries(null);
    setSelectedColor(null);
    setShowColorPicker(false);
  };

  useEscapeKey(() => {
    if (editingChild) setEditingChild(null);
    else if (showForm) handleCancel();
    else if (viewingBookings) setViewingBookings(null);
    else if (viewingDefaultAssignments) setViewingDefaultAssignments(null);
  });

  const toggleExpand = (seriesId: string) => {
    setExpandedSeriesId(prev => prev === seriesId ? null : seriesId);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">{t('admin.eventsTitle')}</h2>
        <div className="relative" ref={exportRef}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.json"
            onChange={handleFileUpload}
            className="hidden"
          />
          <button
            onClick={() => setShowExportMenu(!showExportMenu)}
            className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
            title={t('admin.actions')}
          >
            <span className="flex flex-col gap-[3px]">
              <span className="w-1 h-1 bg-gray-600 rounded-full" />
              <span className="w-1 h-1 bg-gray-600 rounded-full" />
              <span className="w-1 h-1 bg-gray-600 rounded-full" />
            </span>
          </button>
          {showExportMenu && (
            <div className="absolute right-0 top-10 z-20 bg-white border rounded-lg shadow-lg py-1 min-w-[160px]">
              <button
                onClick={() => {
                  setShowForm(true);
                  setEditingSeries(null);
                  setSelectedColor(null);
                  setShowColorPicker(false);
                  setShowExportMenu(false);
                }}
                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 font-medium"
              >
                {t('admin.createNewEvent')}
              </button>
              <hr className="my-1" />
              <button
                onClick={() => { slotsAPI.downloadSlotTemplate('csv'); setShowExportMenu(false); }}
                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
              >
                {t('admin.exportCsv')}
              </button>
              <button
                onClick={() => { slotsAPI.downloadSlotTemplate('json'); setShowExportMenu(false); }}
                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
              >
                {t('admin.exportJson')}
              </button>
              <hr className="my-1" />
              <button
                onClick={() => { fileInputRef.current?.click(); setShowExportMenu(false); }}
                disabled={bulkUploadMutation.isPending}
                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                {bulkUploadMutation.isPending ? t('admin.importing') : t('admin.import')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Upload result banner */}
      {uploadResult && (
        <div className="mb-4 p-4 rounded-lg border bg-white shadow-sm">
          <div className="flex justify-between items-start">
            <div>
              <p className="font-medium text-gray-800">
                {t('admin.importComplete')} <span className="text-green-600">{t('admin.createdCount', { count: uploadResult.created })}</span>
                {(uploadResult as any).skipped > 0 && (
                  <>, <span className="text-yellow-600">{t('admin.skippedCount', { count: (uploadResult as any).skipped })}</span></>
                )}
                {uploadResult.failed > 0 && (
                  <>, <span className="text-red-600">{t('admin.failedCount', { count: uploadResult.failed })}</span></>
                )}
              </p>
              {uploadResult.failures.length > 0 && (
                <ul className="mt-2 text-sm text-red-600 space-y-1">
                  {uploadResult.failures.map((f: any, i: number) => (
                    <li key={i}>Row {f.row}: {f.reason}</li>
                  ))}
                </ul>
              )}
            </div>
            <button
              onClick={() => setUploadResult(null)}
              className="text-gray-400 hover:text-gray-600 text-lg leading-none"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Full-screen modal overlay for Create / Edit */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">
                {editingSeries ? t('admin.editEventSeries') : t('admin.createNewEvent')}
              </h3>
              <button
                onClick={handleCancel}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.type')}</label>
                  <div className="flex gap-2">
                    <input type="hidden" name="type" value="BIKING" />
                    <div className="flex-1 border rounded px-3 py-2 bg-gray-50 text-gray-700">{t('admin.typeBiking')}</div>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => { setShowColorPicker(!showColorPicker); if (!showColorPicker) setCustomColorStaging(selectedColor || '#DBEAFE'); }}
                        className="w-10 h-10 rounded border-2 border-gray-300 hover:border-gray-400 transition-colors flex-shrink-0"
                        style={{ backgroundColor: selectedColor || '#E5E7EB' }}
                        title={selectedColor ? t('admin.customColor', { color: selectedColor }) : t('admin.setCustomColor')}
                      />
                      {showColorPicker && (
                        <div className="absolute top-12 right-0 sm:left-0 sm:right-auto z-50 bg-white border rounded-lg shadow-lg p-3 w-56">
                          {/* Preset swatches */}
                          <div className="grid grid-cols-6 gap-1.5 mb-2">
                            {COLOR_PALETTE.map((color) => (
                              <button
                                key={color}
                                type="button"
                                onClick={() => {
                                  setSelectedColor(color);
                                  addRecentColor(color);
                                  setShowColorPicker(false);
                                }}
                                className={`w-7 h-7 rounded border-2 transition-transform hover:scale-110 ${
                                  selectedColor === color ? 'border-gray-800 ring-1 ring-gray-400' : 'border-gray-200'
                                }`}
                                style={{ backgroundColor: color }}
                                title={color}
                              />
                            ))}
                          </div>

                          {/* Recent colors */}
                          {recentColors.length > 0 && (
                            <div className="border-t mt-1 pt-2 mb-2">
                              <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">{t('admin.recentColors')}</div>
                              <div className="flex gap-1.5">
                                {recentColors.map((color) => (
                                  <button
                                    key={color}
                                    type="button"
                                    onClick={() => {
                                      setSelectedColor(color);
                                      setShowColorPicker(false);
                                    }}
                                    className={`w-7 h-7 rounded border-2 transition-transform hover:scale-110 ${
                                      selectedColor === color ? 'border-gray-800 ring-1 ring-gray-400' : 'border-gray-200'
                                    }`}
                                    style={{ backgroundColor: color }}
                                    title={color}
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Copy from existing event — searchable dropdown */}
                          {seriesWithColors.length > 0 && (
                            <div className="border-t mt-1 pt-2 mb-2" ref={eventColorDropdownRef}>
                              <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">{t('admin.fromEvents')}</div>
                              <div className="relative">
                                <input
                                  type="text"
                                  value={eventColorFilter}
                                  onChange={(e) => { setEventColorFilter(e.target.value); setEventColorDropdownOpen(true); }}
                                  onFocus={() => setEventColorDropdownOpen(true)}
                                  placeholder={t('admin.searchEvents')}
                                  className="w-full border rounded px-2 py-1 text-xs"
                                />
                                {eventColorDropdownOpen && filteredSeriesColors.length > 0 && (
                                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-32 overflow-y-auto">
                                    {filteredSeriesColors.map((s) => (
                                      <button
                                        key={s.id}
                                        type="button"
                                        onClick={() => {
                                          setSelectedColor(s.color!);
                                          addRecentColor(s.color!);
                                          setEventColorFilter('');
                                          setEventColorDropdownOpen(false);
                                          setShowColorPicker(false);
                                        }}
                                        className="w-full text-left px-2 py-1.5 hover:bg-gray-50 text-xs flex items-center gap-2"
                                      >
                                        <span
                                          className="w-4 h-4 rounded border border-gray-200 flex-shrink-0"
                                          style={{ backgroundColor: s.color! }}
                                        />
                                        <span className="truncate">{s.label}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                                {eventColorDropdownOpen && eventColorFilter && filteredSeriesColors.length === 0 && (
                                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg px-2 py-1.5 text-xs text-gray-500">
                                    {t('admin.noMatchingEvents')}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Custom color picker */}
                          <div className="flex items-center gap-2 border-t mt-1 pt-2">
                            <input
                              type="color"
                              value={customColorStaging}
                              onChange={(e) => setCustomColorStaging(e.target.value)}
                              className="w-7 h-7 rounded border border-gray-300 cursor-pointer p-0 flex-shrink-0"
                            />
                            <span className="text-xs text-gray-500 flex-1 font-mono">{customColorStaging}</span>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedColor(customColorStaging);
                                addRecentColor(customColorStaging);
                                setShowColorPicker(false);
                              }}
                              className="text-xs bg-primary-600 text-white px-2 py-1 rounded hover:bg-primary-700"
                            >
                              {t('admin.apply')}
                            </button>
                          </div>

                          {/* Reset */}
                          {selectedColor && (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedColor(null);
                                setShowColorPicker(false);
                              }}
                              className="w-full text-xs text-gray-600 hover:text-gray-800 py-1 border-t mt-1 pt-2"
                            >
                              {t('admin.resetToDefault')}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.title')}</label>
                  <input type="text" name="title" required className="w-full border rounded px-3 py-2"
                    placeholder={t('admin.titlePlaceholder')} defaultValue={editingSeries?.title || ""} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.startDate')}</label>
                  <input type="date" name="date" required className="w-full border rounded px-3 py-2"
                    defaultValue={editingSeries ? format(new Date(editingSeries.startDate), 'yyyy-MM-dd') : ""} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.startTime')}</label>
                  <input type="time" name="startTime" required className="w-full border rounded px-3 py-2"
                    defaultValue={editingSeries?.startTime || ""} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.endTime')}</label>
                  <input type="time" name="endTime" className="w-full border rounded px-3 py-2"
                    defaultValue={editingSeries?.endTime || ""} />
                  <p className="text-xs text-gray-500 mt-1">{t('admin.optional')}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.capacity')}</label>
                  <input type="number" name="capacity" required min="1"
                    defaultValue={editingSeries?.capacity || 10} className="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.recurrencePattern')}</label>
                  <select name="recurrencePattern" className="w-full border rounded px-3 py-2"
                    defaultValue={editingSeries?.recurrencePattern || "NONE"}>
                    <option value="NONE">{t('admin.recNone')}</option>
                    <option value="DAILY">{t('admin.recDaily')}</option>
                    <option value="WEEKLY">{t('admin.recWeekly')}</option>
                    <option value="MONTHLY">{t('admin.recMonthly')}</option>
                    <option value="YEARLY">{t('admin.recYearly')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.recurrenceEndDate')}</label>
                  <input type="date" name="recurrenceEndDate" className="w-full border rounded px-3 py-2"
                    defaultValue={editingSeries?.recurrenceEndDate ? format(new Date(editingSeries.recurrenceEndDate), 'yyyy-MM-dd') : ""} />
                  <p className="text-xs text-gray-500 mt-1">{t('admin.optional')}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.occurrences')}</label>
                  <input type="number" name="recurrenceCount" min="1" max="365"
                    className="w-full border rounded px-3 py-2" placeholder={t('admin.occurrencesPlaceholder')}
                    defaultValue={editingSeries?.recurrenceCount || ""} />
                  <p className="text-xs text-gray-500 mt-1">{t('admin.occurrencesHint')}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.description')}</label>
                <textarea name="description" className="w-full border rounded px-3 py-2" rows={3}
                  defaultValue={editingSeries?.description || ""} />
              </div>
              <div className="flex flex-wrap gap-3 pt-2">
                <button type="submit" className="bg-primary-600 text-white px-6 py-2 rounded hover:bg-primary-700">
                  {editingSeries ? t('admin.updateEvent') : t('admin.createEvent')}
                </button>
                <button type="button" onClick={handleCancel}
                  className="border border-gray-300 text-gray-700 px-6 py-2 rounded hover:bg-gray-50">
                  {t('admin.cancel')}
                </button>
                {editingSeries && (
                  <button
                    type="button"
                    onClick={() => {
                      setViewingDefaultAssignments({
                        id: editingSeries.id,
                        title: editingSeries.title,
                        capacity: editingSeries.capacity,
                      });
                    }}
                    className="w-full sm:w-auto sm:ml-auto flex items-center justify-center gap-2 border border-primary-300 text-primary-700 px-4 py-2 rounded hover:bg-primary-50 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                    </svg>
                    {t('admin.assignedUsers')}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('admin.searchByTitle')}
            className="w-full border rounded-lg pl-8 pr-8 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        {searchQuery && (
          <span className="text-xs text-gray-500">
            {t('admin.eventsOf', { count: filteredSeries.length, total: seriesList.length })}
          </span>
        )}
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Desktop: Table */}
        <div className="hidden lg:block">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {([
                  ['date', t('admin.dayAndTime')] as const,
                  ['type', t('admin.type')] as const,
                  ['title', t('admin.title')] as const,
                  ['info', t('admin.info')] as const,
                ]).map(([col, label]) => (
                  <th
                    key={col}
                    onClick={() => handleSort(col)}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:bg-gray-100 transition-colors"
                  >
                    <span className="inline-flex items-center gap-1">
                      {label}
                      {sortColumn === col ? (
                        <svg className="w-3.5 h-3.5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          {sortDirection === 'asc'
                            ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          }
                        </svg>
                      ) : (
                        <svg className="w-3.5 h-3.5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                        </svg>
                      )}
                    </span>
                  </th>
                ))}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSeries.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                    {seriesList.length === 0
                      ? t('admin.noEventsCreate')
                      : t('admin.noMatchFilters')}
                  </td>
                </tr>
              ) : (
                paginatedSeries.map((series) => (
                  <React.Fragment key={series.id}>
                    <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => toggleExpand(series.id)}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {DAYS[new Date(series.startDate).getDay()]}
                        <br />
                        <span className="text-gray-500">
                          {series.startTime}{series.endTime ? ` - ${series.endTime}` : ''}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{series.type}</td>
                      <td className="px-6 py-4 text-sm">
                        {series.title}
                        {series.recurrencePattern !== 'NONE' && (
                          <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                            {series.recurrencePattern}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {series._count?.defaultAssignments ?? 0} {t('admin.defaults')} | {t('admin.capacity')}: {series.capacity}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm" onClick={(e) => e.stopPropagation()}>
                        <div className="relative" data-kebab>
                          <button
                            onClick={(e) => {
                              if (openSeriesMenu === series.id) {
                                setOpenSeriesMenu(null);
                              } else {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setSeriesMenuPos({ top: rect.bottom + 4, left: rect.right - 192 });
                                setOpenSeriesMenu(series.id);
                              }
                            }}
                            className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100"
                          >
                            <span className="flex flex-col gap-[3px]">
                              <span className="w-1 h-1 bg-gray-500 rounded-full" />
                              <span className="w-1 h-1 bg-gray-500 rounded-full" />
                              <span className="w-1 h-1 bg-gray-500 rounded-full" />
                            </span>
                          </button>
                          {openSeriesMenu === series.id && ReactDOM.createPortal(
                            <div
                              data-kebab
                              className="fixed z-[9999] bg-white border rounded-lg shadow-lg py-1 w-48"
                              style={{ top: seriesMenuPos.top, left: seriesMenuPos.left }}
                            >
                              <button
                                onClick={() => { handleEdit(series); setOpenSeriesMenu(null); }}
                                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                              >
                                {t('admin.editSeries')}
                              </button>
                              <button
                                onClick={() => { setViewingDefaultAssignments({ id: series.id, title: series.title, capacity: series.capacity }); setOpenSeriesMenu(null); }}
                                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                              >
                                {t('admin.bookingsFor')}
                              </button>
                              <hr className="my-1" />
                              <button
                                onClick={() => {
                                  setOpenSeriesMenu(null);
                                  if (window.confirm(t('admin.deleteSeriesConfirm'))) {
                                    deleteMutation.mutate(series.id);
                                  }
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                              >
                                {t('admin.deleteSeries')}
                              </button>
                            </div>,
                            document.body
                          )}
                        </div>
                      </td>
                    </tr>
                    {/* Expanded children */}
                    {expandedSeriesId === series.id && (
                      <tr>
                        <td colSpan={5} className="px-6 py-4 bg-gray-50">
                          <div className="text-sm font-semibold text-gray-700 mb-2">
                            {t('admin.occurrencesLabel')}
                          </div>
                          {expandedChildren.length === 0 ? (
                            <p className="text-gray-400 text-sm">{t('admin.noChildSlots')}</p>
                          ) : (
                            <div className="max-h-64 overflow-y-auto">
                              <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">{t('admin.date')}</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">{t('admin.startTime')}</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">{t('admin.bookingsFor')}</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">{t('admin.status')}</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">{t('admin.actions')}</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {expandedChildren.map((child: any) => (
                                    <tr key={child.id} className={child.cancelled ? 'opacity-50' : ''}>
                                      <td className="px-4 py-2">{format(new Date(child.date), 'dd MMM yyyy')}</td>
                                      <td className="px-4 py-2">{child.startTime}{child.endTime ? ` - ${child.endTime}` : ''}</td>
                                      <td className="px-4 py-2">
                                        {child._count?.bookings ?? child.bookings?.length ?? 0}/{child.capacity}
                                      </td>
                                      <td className="px-4 py-2">
                                        {child.cancelled ? (
                                          <span className="text-red-600 font-medium">{t('admin.cancelled')}</span>
                                        ) : (
                                          <span className="text-green-600">{t('admin.activeStatus')}</span>
                                        )}
                                      </td>
                                      <td className="px-4 py-2">
                                        <div className="relative" data-kebab>
                                          <button
                                            onClick={() => setOpenChildMenu(openChildMenu === child.id ? null : child.id)}
                                            className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-100"
                                          >
                                            <span className="flex flex-col gap-[2px]">
                                              <span className="w-0.5 h-0.5 bg-gray-500 rounded-full" />
                                              <span className="w-0.5 h-0.5 bg-gray-500 rounded-full" />
                                              <span className="w-0.5 h-0.5 bg-gray-500 rounded-full" />
                                            </span>
                                          </button>
                                          {openChildMenu === child.id && (
                                            <div className="absolute right-0 top-7 z-20 bg-white border rounded-lg shadow-lg py-1 min-w-[140px]">
                                              <button
                                                onClick={() => { setEditingChild(child); setOpenChildMenu(null); }}
                                                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                                              >
                                                {t('admin.edit')}
                                              </button>
                                              <button
                                                onClick={() => { setViewingBookings({ id: child.id, title: `${series.title} - ${format(new Date(child.date), 'dd MMM yyyy')}` }); setOpenChildMenu(null); }}
                                                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                                              >
                                                {t('admin.bookings')}
                                              </button>
                                              {child.cancelled && (
                                                <>
                                                  <hr className="my-1" />
                                                  <button
                                                    onClick={() => {
                                                      setOpenChildMenu(null);
                                                      if (window.confirm(t('admin.restoreEventConfirm'))) {
                                                        restoreMutation.mutate(child.id);
                                                      }
                                                    }}
                                                    className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50"
                                                  >
                                                    {t('admin.restoreEvent')}
                                                  </button>
                                                </>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile/Tablet: Cards */}
        <div className="lg:hidden divide-y divide-gray-200">
          {filteredSeries.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500">
              {seriesList.length === 0
                ? t('admin.noEventsCreate')
                : t('admin.noMatchFilters')}
            </div>
          ) : (
            paginatedSeries.map((series) => (
              <div key={series.id}>
                <div
                  className="p-4 space-y-2 active:bg-gray-50 cursor-pointer"
                  onClick={() => toggleExpand(series.id)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900">
                        {series.title}
                        {series.recurrencePattern !== 'NONE' && (
                          <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                            {series.recurrencePattern}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {DAYS[new Date(series.startDate).getDay()]} {series.startTime}{series.endTime ? ` - ${series.endTime}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      <span className="text-xs text-gray-500">{series.type}</span>
                      <div className="relative" data-kebab>
                        <button
                          onClick={(e) => {
                            if (openSeriesMenu === series.id) {
                              setOpenSeriesMenu(null);
                            } else {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setSeriesMenuPos({ top: rect.bottom + 4, left: Math.min(rect.right - 192, window.innerWidth - 200) });
                              setOpenSeriesMenu(series.id);
                            }
                          }}
                          className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100"
                        >
                          <span className="flex flex-col gap-[3px]">
                            <span className="w-1 h-1 bg-gray-500 rounded-full" />
                            <span className="w-1 h-1 bg-gray-500 rounded-full" />
                            <span className="w-1 h-1 bg-gray-500 rounded-full" />
                          </span>
                        </button>
                        {openSeriesMenu === series.id && ReactDOM.createPortal(
                          <div
                            data-kebab
                            className="fixed z-[9999] bg-white border rounded-lg shadow-lg py-1 w-48"
                            style={{ top: seriesMenuPos.top, left: seriesMenuPos.left }}
                          >
                            <button
                              onClick={() => { handleEdit(series); setOpenSeriesMenu(null); }}
                              className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                            >
                              {t('admin.editSeries')}
                            </button>
                            <button
                              onClick={() => { setViewingDefaultAssignments({ id: series.id, title: series.title, capacity: series.capacity }); setOpenSeriesMenu(null); }}
                              className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                            >
                              {t('admin.bookings')}
                            </button>
                            <hr className="my-1" />
                            <button
                              onClick={() => {
                                setOpenSeriesMenu(null);
                                if (window.confirm(t('admin.deleteSeriesConfirm'))) {
                                  deleteMutation.mutate(series.id);
                                }
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                            >
                              {t('admin.deleteSeries')}
                            </button>
                          </div>,
                          document.body
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                    <span>{series._count?.defaultAssignments ?? 0} {t('admin.defaults')}</span>
                    <span>{t('admin.capacity')}: {series.capacity}</span>
                  </div>
                </div>
                {/* Expanded children - mobile */}
                {expandedSeriesId === series.id && (
                  <div className="px-4 pb-4 bg-gray-50 space-y-2">
                    <div className="text-xs font-semibold text-gray-700 pt-2">{t('admin.occurrencesLabel')}</div>
                    {expandedChildren.length === 0 ? (
                      <p className="text-gray-400 text-xs">{t('admin.noChildSlots')}</p>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {expandedChildren.map((child: any) => (
                          <div
                            key={child.id}
                            className={`bg-white border rounded-lg p-3 text-sm ${child.cancelled ? 'opacity-50' : ''}`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="font-medium">{format(new Date(child.date), 'dd MMM yyyy')}</span>
                                <span className="text-gray-500 ml-2">{child.startTime}{child.endTime ? `-${child.endTime}` : ''}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">
                                  {child._count?.bookings ?? child.bookings?.length ?? 0}/{child.capacity}
                                </span>
                                {child.cancelled ? (
                                  <span className="text-xs text-red-600 font-medium">{t('admin.cancelled')}</span>
                                ) : (
                                  <span className="text-xs text-green-600">{t('admin.activeStatus')}</span>
                                )}
                                <div className="relative" data-kebab>
                                  <button
                                    onClick={() => setOpenChildMenu(openChildMenu === child.id ? null : child.id)}
                                    className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-100"
                                  >
                                    <span className="flex flex-col gap-[2px]">
                                      <span className="w-0.5 h-0.5 bg-gray-500 rounded-full" />
                                      <span className="w-0.5 h-0.5 bg-gray-500 rounded-full" />
                                      <span className="w-0.5 h-0.5 bg-gray-500 rounded-full" />
                                    </span>
                                  </button>
                                  {openChildMenu === child.id && (
                                    <div className="absolute right-0 top-7 z-20 bg-white border rounded-lg shadow-lg py-1 min-w-[140px]">
                                      <button
                                        onClick={() => { setEditingChild(child); setOpenChildMenu(null); }}
                                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                                      >
                                        {t('admin.edit')}
                                      </button>
                                      <button
                                        onClick={() => { setViewingBookings({ id: child.id, title: `${series.title} - ${format(new Date(child.date), 'dd MMM yyyy')}` }); setOpenChildMenu(null); }}
                                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                                      >
                                        {t('admin.bookings')}
                                      </button>
                                      {child.cancelled && (
                                        <>
                                          <hr className="my-1" />
                                          <button
                                            onClick={() => {
                                              setOpenChildMenu(null);
                                              if (window.confirm(t('admin.restoreEventConfirm'))) {
                                                restoreMutation.mutate(child.id);
                                              }
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50"
                                          >
                                            {t('admin.restoreEvent')}
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t bg-gray-50">
            <span className="text-sm text-gray-600">
              {t('admin.showingRange', { from: (currentPage - 1) * PAGE_SIZE + 1, to: Math.min(currentPage * PAGE_SIZE, filteredSeries.length), total: filteredSeries.length })}
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm rounded border hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t('admin.prev')}
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1 text-sm rounded border ${
                    page === currentPage
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm rounded border hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t('admin.next')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bookings Modal */}
      {viewingBookings && (
        <BookingsModal
          slotId={viewingBookings.id}
          slotTitle={viewingBookings.title}
          onClose={() => setViewingBookings(null)}
        />
      )}

      {/* Default Assignments Modal */}
      {viewingDefaultAssignments && (
        <DefaultAssignmentsModal
          slotId={viewingDefaultAssignments.id}
          slotTitle={viewingDefaultAssignments.title}
          slotCapacity={viewingDefaultAssignments.capacity}
          onClose={() => setViewingDefaultAssignments(null)}
        />
      )}

      {/* Edit Child Modal */}
      {editingChild && (
        <EditChildModal
          child={editingChild}
          onClose={() => setEditingChild(null)}
        />
      )}

      {/* Cancellation Recipients Modal — shown after a series is cancelled */}
      {cancellationInfo && (
        <CancellationRecipientsModal
          cancellation={cancellationInfo}
          onClose={() => setCancellationInfo(null)}
        />
      )}
    </div>
  );
};

export default EventsTab;
